
import React, { useState } from 'react';
import { Teacher, normalizeClassName } from '../types';
import { UserPlus, Search, Mail, X, Check, Briefcase, Trash, Key, User } from 'lucide-react';
import ConfirmModal from '../components/ConfirmModal';

interface Props {
  teachers: Teacher[];
  onUpdate: (teachers: Teacher[]) => void;
  availableClasses: string[];
}

const TeachersManagementView: React.FC<Props> = ({ teachers, onUpdate, availableClasses }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTeacher, setEditingTeacher] = useState<Teacher | null>(null);
  
  // Confirmation Modal State
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {}
  });
  
  // Form State
  const [formData, setFormData] = useState<Partial<Teacher>>({
    name: '', username: '', password: '', subject: '', assignedClasses: []
  });

  const filteredTeachers = teachers.filter(t => {
    const name = (t.name || '').toLowerCase();
    const username = t.username || '';
    const term = (searchTerm || '').toLowerCase();
    return name.includes(term) || username.toLowerCase().includes(term);
  });

  const handleOpenModal = (teacher?: Teacher) => {
    if (teacher) {
      setEditingTeacher(teacher);
      setFormData({ ...teacher });
    } else {
      setEditingTeacher(null);
      setFormData({ name: '', username: '', password: '', subject: '', assignedClasses: [] });
    }
    setIsModalOpen(true);
  };

  const handleSave = () => {
    if (!formData.name || !formData.username || !formData.password) {
      alert('Nama, Username, dan Password wajib diisi!');
      return;
    }

    if (editingTeacher) {
      onUpdate(teachers.map(t => t.id === editingTeacher.id ? { ...t, ...formData } as Teacher : t));
    } else {
      const newTeacher: Teacher = {
        ...formData as Teacher,
        id: '', // Temporary ID, will be replaced by DB
      };
      onUpdate([...teachers, newTeacher]);
    }
    setIsModalOpen(false);
  };

  const handleDelete = (id: string) => {
    const teacher = teachers.find(t => t.id === id);
    setConfirmModal({
      isOpen: true,
      title: 'Hapus Akun Guru',
      message: `Apakah Anda yakin ingin menghapus data guru "${teacher?.name || 'ini'}"? Tindakan ini akan menghapus akses login dan semua data terkait.`,
      onConfirm: () => {
        onUpdate(teachers.filter(t => t.id !== id));
        setConfirmModal(prev => ({ ...prev, isOpen: false }));
      }
    });
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="bg-white dark:bg-slate-900 p-6 sm:p-8 rounded-[2.5rem] shadow-sm border border-slate-200 dark:border-slate-800">
        <div className="flex flex-col sm:flex-row justify-between items-center mb-10 gap-6">
          <div>
              <h2 className="text-3xl font-black text-slate-800 dark:text-white tracking-tight leading-none mb-2">Manajemen Akun Guru</h2>
              <p className="text-sm text-slate-500 dark:text-slate-400 font-medium">Atur profil, login, dan plotting kelas guru.</p>
          </div>
          <div className="flex items-center gap-4 w-full sm:w-auto">
            <div className="relative flex-1 sm:w-72">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
              <input
                type="text"
                placeholder="Cari Username / Nama..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-3.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl outline-none focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 text-sm font-bold dark:text-white transition-all shadow-inner"
              />
            </div>
            <button 
              onClick={() => handleOpenModal()}
              className="flex items-center justify-center gap-2 px-8 py-3.5 bg-indigo-600 text-white font-black rounded-2xl hover:bg-indigo-700 transition-all shadow-xl shadow-indigo-500/20 active:scale-95"
            >
              <UserPlus size={18} />
              <span className="hidden sm:inline text-sm">Input Guru</span>
            </button>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredTeachers.map(teacher => (
              <div key={teacher.id} className="group p-8 bg-white dark:bg-slate-800/50 border border-slate-200 dark:border-slate-800 rounded-[2rem] hover:border-indigo-500 dark:hover:border-indigo-400 hover:shadow-2xl hover:shadow-indigo-500/10 transition-all relative overflow-hidden flex flex-col h-full">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-600 opacity-[0.03] rounded-full -mr-16 -mt-16 group-hover:scale-150 transition-transform duration-700"></div>
                  
                  <div className="flex items-start justify-between mb-6 relative z-10">
                      <div className="w-16 h-16 bg-indigo-600 dark:bg-indigo-500/10 rounded-2xl flex items-center justify-center font-black text-white dark:text-indigo-400 text-3xl shadow-xl shadow-indigo-500/20 group-hover:rotate-3 transition-transform">
                          {teacher.name[0]}
                      </div>
                      <div className="flex flex-col items-end">
                         <div className="px-3 py-1 bg-indigo-500/10 rounded-full border border-indigo-500/20 mb-2">
                           <p className="text-[10px] font-black text-indigo-500 uppercase tracking-widest">{teacher.subject}</p>
                         </div>
                         {teacher.homeroomClass && (
                           <div className="px-3 py-1 bg-emerald-500/10 rounded-full border border-emerald-500/20 mb-2">
                             <p className="text-[10px] font-black text-emerald-600 dark:text-emerald-400 uppercase tracking-widest">Wali Kelas {teacher.homeroomClass}</p>
                           </div>
                         )}
                         <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">User: {teacher.username}</p>
                      </div>
                  </div>

                  <h4 className="font-black text-slate-800 dark:text-white text-xl tracking-tight mb-4 group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors">{teacher.name}</h4>

                  <div className="flex items-center justify-between pt-6 border-t border-slate-100 dark:border-slate-800 relative z-10 mt-auto">
                      <div className="flex gap-2">
                         <div className="p-2.5 bg-slate-50 dark:bg-slate-900 rounded-xl border border-slate-100 dark:border-slate-800">
                           <Key size={14} className="text-slate-400" />
                         </div>
                         <div className="flex flex-col justify-center">
                            <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">Pass Hash</p>
                            <p className="text-[10px] font-bold text-slate-600 dark:text-slate-400">********</p>
                         </div>
                      </div>
                      <div className="flex gap-3">
                          <button 
                            onClick={() => handleOpenModal(teacher)}
                            className="px-6 py-2.5 bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 text-xs font-black rounded-2xl hover:bg-indigo-600 hover:text-white transition-all active:scale-95 border border-indigo-100 dark:border-indigo-500/20"
                          >
                            Edit Profil
                          </button>
                          <button 
                            onClick={() => handleDelete(teacher.id)}
                            className="p-3 bg-rose-50 dark:bg-rose-500/10 text-rose-500 rounded-2xl hover:bg-rose-500 hover:text-white transition-all active:scale-95 border border-rose-100 dark:border-rose-500/20"
                          >
                            <Trash size={18}/>
                          </button>
                      </div>
                  </div>
              </div>
          ))}
        </div>
      </div>

      {/* Modal Add/Edit */}
      {isModalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-950/80 backdrop-blur-md" onClick={() => setIsModalOpen(false)}></div>
          <div className="bg-white dark:bg-slate-900 w-full max-w-md max-h-[90vh] rounded-[2.5rem] shadow-2xl relative flex flex-col overflow-hidden animate-fade-in-up border border-white/10">
            <div className="bg-indigo-600 p-10 text-white relative">
               <button onClick={() => setIsModalOpen(false)} className="absolute top-8 right-8 p-3 bg-white/10 rounded-full hover:bg-white/20 transition-colors"><X size={20}/></button>
               <h3 className="text-3xl font-black tracking-tight leading-none">{editingTeacher ? 'Perbarui Akun Guru' : 'Registrasi Guru Baru'}</h3>
               <p className="text-indigo-100 text-sm font-medium mt-3 opacity-80">Konfigurasi hak akses login dan penempatan mengajar.</p>
            </div>
            <div className="p-6 sm:p-8 overflow-y-auto flex-1">
               <div className="grid grid-cols-1 gap-8">
                  <div className="space-y-5">
                    <p className="text-[10px] font-black text-indigo-500 uppercase tracking-[0.2em] mb-4">Biodata & Login</p>
                    <div>
                        <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">Nama Lengkap & Gelar</label>
                        <input 
                          type="text" 
                          value={formData.name}
                          onChange={e => setFormData({...formData, name: e.target.value})}
                          className="w-full px-5 py-4 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 outline-none transition-all dark:text-white font-bold"
                          placeholder="Ega Pratama, S.Pd."
                        />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1 flex items-center gap-1.5"><User size={10}/> Username</label>
                            <input 
                            type="text" 
                            value={formData.username}
                            onChange={e => setFormData({...formData, username: e.target.value})}
                            className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 outline-none transition-all dark:text-white font-bold"
                            placeholder="364"
                            />
                        </div>
                        <div>
                            <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1 flex items-center gap-1.5"><Key size={10}/> Password</label>
                            <input 
                            type="password" 
                            value={formData.password}
                            onChange={e => setFormData({...formData, password: e.target.value})}
                            className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 outline-none transition-all dark:text-white font-bold"
                            placeholder="***"
                            />
                        </div>
                    </div>
                    <div>
                        <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">Mata Pelajaran</label>
                        <input 
                          type="text" 
                          value={formData.subject}
                          onChange={e => setFormData({...formData, subject: e.target.value})}
                          className="w-full px-5 py-4 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 outline-none transition-all dark:text-white font-bold"
                          placeholder="Informatika"
                        />
                    </div>
                    <div>
                        <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">Wali Kelas (Kelas Perwalian)</label>
                        <select
                          value={formData.homeroomClass || ''}
                          onChange={e => setFormData({...formData, homeroomClass: e.target.value || undefined})}
                          className="w-full px-5 py-4 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 outline-none transition-all dark:text-white font-bold"
                        >
                          <option value="">-- Bukan Wali Kelas / Belum Ditentukan --</option>
                          {Array.from(new Set((availableClasses || []).map(c => normalizeClassName(c)).filter(Boolean))).map(cls => (
                            <option key={cls} value={cls}>Wali Kelas {cls}</option>
                          ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">Penempatan Kelas ({Array.from(new Set((availableClasses || []).map(c => normalizeClassName(c)).filter(Boolean))).length})</label>
                        <div className="grid grid-cols-3 gap-2 max-h-40 overflow-y-auto p-1 border border-slate-100 dark:border-slate-800 rounded-2xl bg-slate-50/50 dark:bg-slate-800/50">
                          {Array.from(new Set((availableClasses || []).map(c => normalizeClassName(c)).filter(Boolean))).map(cls => {
                            const assignedNorm = (formData.assignedClasses || []).map(c => normalizeClassName(c));
                            const isSelected = assignedNorm.includes(cls);
                            return (
                              <button
                                key={cls}
                                type="button"
                                onClick={() => {
                                  const current = formData.assignedClasses || [];
                                  let updated: string[];
                                  if (isSelected) {
                                    updated = current.filter(c => normalizeClassName(c) !== cls);
                                  } else {
                                    updated = [...current, cls];
                                  }
                                  const cleanUpdated = Array.from(new Set(updated.map(c => normalizeClassName(c)).filter(Boolean)))
                                    .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
                                  setFormData({...formData, assignedClasses: cleanUpdated});
                                }}
                                className={`px-3 py-2 rounded-xl text-xs font-bold transition-all ${
                                  isSelected
                                    ? 'bg-indigo-600 text-white'
                                    : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400'
                                }`}
                              >
                                {cls}
                              </button>
                            );
                          })}
                        </div>
                    </div>
                  </div>
               </div>

               <button 
                onClick={handleSave}
                className="w-full mt-10 py-5 bg-indigo-600 text-white font-black rounded-[2rem] hover:bg-indigo-700 transition-all shadow-2xl shadow-indigo-600/30 active:scale-95 flex items-center justify-center gap-3"
               >
                 <Check size={24} />
                 SIMPAN & UPDATE AKUN
               </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
      />
    </div>
  );
};

export default TeachersManagementView;
